<template>
  <div v-if="blogPost" class="container bg-white mx-auto grid grid-flow-col auto-cols-auto gap-16">
    <div class="text mx-auto pb-8">
      <p class="mx-8 hidden md:block crumb">
        <NuxtLink to="/">Home</NuxtLink> /
        <NuxtLink to="/learn/">Learn</NuxtLink> /
        <NuxtLink to="/learn/blogs/">Blogs</NuxtLink> /
        <NuxtLink :to="'/learn/blogs/' + blogPost.slug + '/'">{{ blogPost.title }}</NuxtLink>
      </p>
      
      <h1>{{ blogPost.title }}</h1>

      <div class="text-right shareit">
        <div class="inline-block align-text-top pr-3"><p>Share this post:</p></div>
        <div class="inline-block pb-4">
          <div class="sharethis-inline-share-buttons"></div>
        </div>
      </div>

      <ClientOnly>
        <div v-html="blogPost.content"></div>
      </ClientOnly>
    </div>

    <Sidebar :tags="tags" class="hidden md:block"/>
  </div>
</template>

<script setup lang="ts">
import { useSeoHead, buildBlogPostingSchema } from '~/composables/useSeo';

const { $sg, $config } = useNuxtApp();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();

const slug = computed(() => route.params.slug as string);

// SSR-friendly data fetch for blog post
const { data: postData } = await useAsyncData(
  `blog-post-${slug.value}`,
  () => $sg.getOctoberData(`posts/${slug.value}/slug`),
  { watch: [slug] }
);

// SSR-friendly data fetch for tags
const { data: tags } = await useAsyncData(
  "blog-tags",
  () => $sg.getOctoberData("tags"),
  { default: () => [] }
);

// Handle redirects and not found
if (!postData.value || postData.value.length === 0) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true });
}
if (postData.value[0]?.redirect_url) {
  await navigateTo(`/learn/blogs/${postData.value[0].redirect_url}`, { redirectCode: 301 });
}

const blogPost = computed(() => postData.value?.[0] ?? null);

// Extract first sentence from summary for fallback description
const defaultDescription = computed(() => {
  if (!blogPost.value?.summary) {
    return 'For more than 45 years, Spring Green has been providing professional lawn care, pest control and tree care services. Visit www.spring-green.com to learn more about our dedication to quality.';
  }
  const regex = /<p>(.*?)<\/p>/g;
  const matches = blogPost.value.summary.match(regex);
  if (matches) {
    for (const match of matches) {
      const paragraphText = match.replace(/<\/?p>/g, '').trim();
      if (paragraphText) {
        const sentences = paragraphText.match(/[^\.!\?]+[\.!\?]+/g);
        if (sentences && sentences.length > 0) {
          return sentences[0];
        }
      }
    }
  }
  return 'For more than 45 years, Spring Green has been providing professional lawn care, pest control and tree care services.';
});

// Compute frontend URL for structured data
const frontEndUrl = computed(() =>
  (runtimeConfig.public?.frontEndUrl || "https://www.spring-green.com").replace(/\/$/, "")
);

const blogUrl = computed(() => `${frontEndUrl.value}/learn/blogs/${slug.value}/`);

const featuredImage = computed(() => {
  if (!blogPost.value?.featured_images?.length) return undefined;
  return `${$config.public.sgCmsImage}${blogPost.value.featured_images[0].path}`;
});

// Build BlogPosting structured data
const blogPostingSD = computed(() => {
  if (!blogPost.value) return null;
  return buildBlogPostingSchema({
    headline: blogPost.value.title,
    description: blogPost.value.meta_description || blogPost.value.excerpt || defaultDescription.value,
    datePublished: blogPost.value.published_at,
    dateModified: blogPost.value.updated_at,
    image: featuredImage.value,
    articleSection: blogPost.value.categories?.[0]?.name || "Blog",
    url: blogUrl.value,
  });
});

// Apply SEO head
watchEffect(() => {
  if (!blogPost.value) return;
  
  useSeoHead({
    title: blogPost.value.seo_title || (blogPost.value.title ? `${blogPost.value.title} | Blog | Spring Green` : "Blog | Spring-Green"),
    description: blogPost.value.meta_description || blogPost.value.excerpt || defaultDescription.value,
    openGraph: {
      title: blogPost.value.social_title || blogPost.value.title,
      description: blogPost.value.social_description || blogPost.value.meta_description || blogPost.value.excerpt,
      image: featuredImage.value,
      url: blogUrl.value,
      type: "article",
    },
    twitter: {
      title: blogPost.value.title,
      description: blogPost.value.meta_description || blogPost.value.excerpt || defaultDescription.value,
      image: featuredImage.value,
      card: featuredImage.value ? "summary_large_image" : "summary",
    },
    structuredData: [
      blogPostingSD.value,
      blogPost.value.structured_data,
    ].filter(Boolean),
  });
});
</script>

<style scoped>
.crumb {
  margin: 2rem 0 0;
}

h1{
  font-size: 32px;
}
</style>
